import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { insightApi } from '../api/InsightApi'
import type { InsightElement, InsightResult, InsightStatus } from '../models/insight.types'
import type { InsightStatusResponse } from '../models/insightApi.types'
import { useInsightStore, type InsightStore } from '../stores/insightStore'

import type { InsightsState } from './useInsights'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

interface InsightDetailApiPort {
  get: (
    accountId: number,
    workspaceId: number,
    insightId: number,
    tab?: string,
    signal?: AbortSignal,
  ) => Promise<InsightResult>
  updateStatus: (
    accountId: number,
    workspaceId: number,
    insightId: number,
    status: InsightStatus,
    signal?: AbortSignal,
  ) => Promise<InsightStatusResponse>
}

export interface UseInsightDetailOptions {
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<string | number | undefined>
  tab?: MaybeRefOrGetter<string | undefined>
  api?: InsightDetailApiPort
  store?: InsightStore
}

export interface UseInsightDetail {
  insight: ComputedRef<InsightResult | null>
  elements: ComputedRef<readonly InsightElement[]>
  state: ComputedRef<InsightsState>
  error: ComputedRef<ApiError | null>
  reload: () => Promise<void>
  updateStatus: (status: InsightStatus) => Promise<void>
}

export function useInsightDetail(
  insightId: MaybeRefOrGetter<number | null | undefined>,
  options: UseInsightDetailOptions = {},
): UseInsightDetail {
  const account = options.accountId ? null : useAccount()
  const workspace = options.workspaceId ? null : useWorkspace()
  const api = options.api ?? insightApi
  const store = options.store ?? useInsightStore()
  const state = ref<InsightsState>('loading')
  const error = ref<ApiError | null>(null)
  let readGeneration = 0
  let writeGeneration = 0
  let readController: AbortController | null = null
  let writeController: AbortController | null = null

  const context = computed(() => ({
    accountId: numericId(options.accountId ? toValue(options.accountId) : account?.accountId.value),
    workspaceId: numericId(
      options.workspaceId ? toValue(options.workspaceId) : workspace?.workspaceId.value,
    ),
    insightId: numericId(toValue(insightId) ?? undefined),
    tab: options.tab ? toValue(options.tab) : undefined,
  }))

  const insight = computed(() => {
    const id = context.value.insightId
    return id === undefined ? null : (store.detail[id] ?? null)
  })

  async function reload(): Promise<void> {
    const request = ++readGeneration
    readController?.abort()
    const current = context.value
    if (current.workspaceId !== undefined) store.ensureWorkspace(current.workspaceId)
    if (
      current.accountId === undefined ||
      current.workspaceId === undefined ||
      current.insightId === undefined
    ) {
      error.value = null
      state.value = 'empty'
      return
    }

    const activeController = new AbortController()
    readController = activeController
    state.value = 'loading'
    error.value = null
    const key = contextKey(current)

    try {
      const result = await api.get(
        current.accountId,
        current.workspaceId,
        current.insightId,
        current.tab,
        activeController.signal,
      )
      if (!ownsRead(request, key, activeController.signal)) return
      if (!store.setDetail(current.workspaceId, result)) return
      state.value = 'ready'
    } catch (cause) {
      if (request !== readGeneration || activeController.signal.aborted) return
      const normalized = toApiError(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  async function updateStatus(status: InsightStatus): Promise<void> {
    const request = ++writeGeneration
    writeController?.abort()
    const current = context.value
    if (
      current.accountId === undefined ||
      current.workspaceId === undefined ||
      current.insightId === undefined
    ) {
      return
    }

    const activeController = new AbortController()
    writeController = activeController
    const key = contextKey(current)
    try {
      const response = await api.updateStatus(
        current.accountId,
        current.workspaceId,
        current.insightId,
        status,
        activeController.signal,
      )
      if (!ownsWrite(request, key, activeController.signal)) return
      if (!store.updateStatus(current.workspaceId, current.insightId, response.status)) return
      store.invalidatePanelCache()
      error.value = null
    } catch (cause) {
      if (request !== writeGeneration || activeController.signal.aborted) return
      const normalized = toApiError(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      throw normalized
    }
  }

  function ownsRead(request: number, key: string, signal: AbortSignal): boolean {
    return request === readGeneration && !signal.aborted && key === contextKey(context.value)
  }

  function ownsWrite(request: number, key: string, signal: AbortSignal): boolean {
    return request === writeGeneration && !signal.aborted && key === contextKey(context.value)
  }

  watch(
    context,
    () => {
      writeGeneration += 1
      writeController?.abort()
      void reload()
    },
    { immediate: true },
  )
  onScopeDispose(() => {
    readGeneration += 1
    writeGeneration += 1
    readController?.abort()
    writeController?.abort()
  })

  return {
    insight,
    elements: computed(() => insight.value?.elements ?? []),
    state: computed(() => state.value),
    error: computed(() => error.value),
    reload,
    updateStatus,
  }
}

function contextKey(context: {
  accountId: number | undefined
  workspaceId: number | undefined
  insightId: number | undefined
  tab: string | undefined
}): string {
  return `${context.accountId ?? ''}:${context.workspaceId ?? ''}:${context.insightId ?? ''}:${context.tab ?? ''}`
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function toApiError(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}
